/**
 * Restrained Clerk chrome for Matrix — enterprise desk, not consumer splash.
 */
export function matrixClerkAppearance(resolved: "light" | "dark") {
	const dark = resolved === "dark";

	return {
		layout: {
			socialButtonsPlacement: "top" as const,
			socialButtonsVariant: "blockButton" as const,
			showOptionalFields: true,
		},
		variables: {
			colorPrimary: dark ? "#60cffb" : "#0b0d0f",
			colorBackground: "transparent",
			colorInputBackground: dark ? "#0b0f13" : "#ffffff",
			colorInputText: dark ? "rgba(255,255,255,0.94)" : "#0b0d0f",
			colorText: dark ? "rgba(255,255,255,0.94)" : "#0b0d0f",
			colorTextSecondary: dark
				? "rgba(160, 176, 188, 0.95)"
				: "#5a6b76",
			colorDanger: dark ? "#ff453a" : "#d70015",
			colorNeutral: dark ? "rgba(255,255,255,0.7)" : "#3d4f5c",
			borderRadius: "0.5rem",
			fontFamily: '"Space Grotesk", ui-sans-serif, system-ui, sans-serif',
			fontSize: "0.9rem",
			spacingUnit: "0.85rem",
		},
		elements: {
			rootBox: { width: "100%" },
			cardBox: {
				width: "100%",
				boxShadow: "none",
				overflow: "visible",
			},
			card: {
				background: "transparent",
				border: "none",
				boxShadow: "none",
				padding: "0",
				overflow: "visible",
			},
			headerTitle: { display: "none" },
			headerSubtitle: { display: "none" },
			logoBox: { display: "none" },
			socialButtonsRoot: {
				overflow: "visible",
			},
			socialButtons: {
				overflow: "visible",
				paddingTop: "0",
			},
			socialButtonsBlockButton: {
				background: dark
					? "color-mix(in oklab, #12171c 88%, transparent)"
					: "#ffffff",
				border: dark
					? "1px solid rgba(255,255,255,0.12)"
					: "1px solid rgba(11,13,15,0.12)",
				boxShadow: "none",
				fontWeight: 600,
				height: "2.75rem",
				overflow: "visible",
				position: "relative",
			},
			lastAuthenticationStrategyBadge: {
				display: "none",
			},
			dividerLine: {
				background: dark
					? "rgba(255,255,255,0.1)"
					: "rgba(11,13,15,0.1)",
			},
			dividerText: {
				color: dark ? "rgba(123,143,156,0.95)" : "#7b8f9c",
				fontSize: "0.7rem",
				textTransform: "uppercase",
				letterSpacing: "0.08em",
			},
			formFieldLabel: {
				fontWeight: 600,
				fontSize: "0.75rem",
				color: dark ? "rgba(160,176,188,0.95)" : "#5a6b76",
			},
			formFieldInput: {
				border: dark
					? "1px solid rgba(255,255,255,0.12)"
					: "1px solid rgba(11,13,15,0.12)",
				boxShadow: "none",
				height: "2.5rem",
			},
			formButtonPrimary: {
				background: dark ? "#60cffb" : "#0b0d0f",
				color: dark ? "#0b0d0f" : "#ffffff",
				fontWeight: 600,
				height: "2.75rem",
				boxShadow: "none",
			},
			footerAction: { display: "none" },
			footer: { display: "none" },
			organizationSwitcherTrigger: {
				border: dark
					? "1px solid rgba(255,255,255,0.12)"
					: "1px solid rgba(11,13,15,0.12)",
			},
			pricingTable: {
				width: "100%",
				overflow: "visible",
			},
			pricingTableCard: {
				overflow: "visible",
			},
		},
	};
}

/**
 * PricingTable in chat / Settings. Sign-in uses transparent cards on a
 * designed surface; plan cards on the dark desk disappear if we reuse that.
 */
export function matrixClerkBillingAppearance(resolved: "light" | "dark") {
	const dark = resolved === "dark";
	const base = matrixClerkAppearance(resolved);
	const surface = dark ? "#12171c" : "#ffffff";
	const border = dark
		? "1px solid rgba(255,255,255,0.12)"
		: "1px solid rgba(11,13,15,0.12)";

	return {
		...base,
		variables: {
			...base.variables,
			colorBackground: surface,
		},
		elements: {
			...base.elements,
			card: {
				...base.elements.card,
				background: surface,
			},
			pricingTableCard: {
				background: surface,
				border,
				boxShadow: "none",
				overflow: "visible",
			},
		},
	};
}
